<template>
	<div class="text-black">
		<div class="container px-0 pt-0 mx-auto lg:pt-12">
			<section v-if="showForm">
				<ContactForm
					class="px-8 lg:px-0"
					@submit-response="handleSubmitResponse"
				/>
				<div class="flex flex-col content-center justify-center w-full divide-y">
					<ContactCardSet
						class="w-full max-w-xl mx-auto mb-8"
						grid-column-classes="grid-cols-1"
						:cards="footerCards"
						button-margin="mt-0"
						content-classes=""
					>
						<template #additional-content>
							<p class="pt-0 text-base text-gray-400">
								{{ t('contact.form.subtitle2') }}
							</p>
						</template>
					</ContactCardSet>
				</div>
			</section>
			<SignupComplete
				v-else
				:thank-you-text="t('contact.thank-you')"
				hide-subtitle
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import { useRoute } from 'vue-router'
import { useHead } from '@vueuse/head'
import { useI18n } from 'vue-i18n'
import {
	STORYBLOK_VERSION,
	COMPANY_NAME,
	COMPANY_PHONE,
	BASE_URL
} from '@/constants/index.js'
import { SUCCESS } from '@/constants/form/states.js'
import type { ContactCardProps } from '@/components/contact/types'
import {
	JSON_LD_LOGO,
	JSON_LD_ORGANIZATION_SAME_AS,
	JSON_LD_ORGANIZATION_REVIEW,
	JSON_LD_HAS_OFFER_CATALOG,
	JSON_LD_ORGANIZATION_CONTACT_POINT,
	JSON_LD_ORGANIZATION_ADDRESS,
	JSON_LD_ORGANIZATION_AREA_SERVED
} from '@/constants/json-ld'
import { useStoryblokWithErrorReporting } from '@/composables/useStoryblok'

const { t, locale } = useI18n({
	useScope: 'global'
})

const route = useRoute()

const { getStory } = useStoryblokWithErrorReporting()

useHead({
	title: t('contact.meta-title'),
	meta: [
		{ name: 'description', content: t('contact.meta-description', { COMPANY_NAME }) },
		{ name: 'url', content: `${BASE_URL}/contact` }
	],
	link: [
		{
			rel: 'icon',
			type: 'image/x-icon',
			href: '/favicon.ico'
		}
	]
})
const story = await getStory('contact', {
	version: STORYBLOK_VERSION,
	language: locale.value
})

const showModal = ref(false)
const { title, description } = story.value?.content?.metatags

useSeoMeta({
	title: () => title,
	ogTitle: () => title,
	description: () => description,
	ogDescription: () => description,
	ogImage: () => '/images/logo.svg',
	ogUrl: () => BASE_URL,
	twitterTitle: () => title,
	twitterDescription: () => description,
	twitterImage: () => '/images/logo.svg',
	twitterCard: () => 'summary_large_image'
})

definePageMeta({
	title: 'Contact Us'
})

const showForm = ref(true)
const handleSubmitResponse = (res: string) => {
	if (res === SUCCESS) { showForm.value = false }
}

const footerCards = computed<ContactCardProps[]>(() => {
	return [
		{
			title: t('contact.card-set.call-us.title'),
			description: t('contact.card-set.call-us.description'),
			action: t('contact.card-set.call-us.action'),
			href: 'tel:7207400888'
			// icon: defineAsyncComponent(() => import('@/components/icon/Phone.vue'))
		}
	]
})

useJsonld({
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: BASE_URL
				}, {
					'@type': 'ListItem',
					position: 2,
					name: 'Contact',
					item: `${BASE_URL}${route.fullPath}`
				}
			]
		},
		{
			'@type': 'Organization',
			name: COMPANY_NAME,
			logo: JSON_LD_LOGO,
			url: BASE_URL,
			telephone: COMPANY_PHONE,
			description: t('meta-description', { COMPANY_NAME }),
			contactPoint: JSON_LD_ORGANIZATION_CONTACT_POINT,
			sameAs: JSON_LD_ORGANIZATION_SAME_AS,
			address: JSON_LD_ORGANIZATION_ADDRESS,
			review: JSON_LD_ORGANIZATION_REVIEW,
			areaServed: JSON_LD_ORGANIZATION_AREA_SERVED,
			hasOfferCatalog: JSON_LD_HAS_OFFER_CATALOG
		}
	]
})
</script>
